import type { MessageKey } from '../i18n/messages.ts';
import type { CountryConfig } from './countryConfig.ts';

/**
 * Copy that only Brazil may see, and the market-neutral twin everyone else gets.
 *
 * Pix is a Brazilian payment rail. Argentina deposits by bank transfer or QR and
 * withdraws to a CVU/CBU, and whatever opens next will have its own rail again.
 * The strings below name Pix outright — a login pill, the guest gates, the FAQ
 * search hint, and the four money FAQs — so an Argentine player read a set of
 * rules that were not theirs (D-03, D-06, D-07, D-08, 2026-09-14).
 *
 * The twin is deliberately *neutral* rather than Argentine: "bank transfer" and
 * "your local currency" hold for any market without Pix, so opening a third one
 * does not mean a third copy of the FAQ. A market that needs its own wording
 * later adds a third column here, not a branch in a screen.
 *
 * Screens never call `t('heroPix')` directly — the guard in
 * `brazilHardcode.guard.test.ts` fails the build if one does. They ask
 * {@link marketCopyKey} for the key, the same way `complyLine` already worked.
 */
const PIX_FREE_TWIN = {
  complyLine: 'complyLineNoPix',
  heroPix: 'heroPixNoPix',
  landingSub: 'landingSubNoPix',
  introPerkFastPix: 'introPerkFastPixNoPix',
  walletTrustSpeed: 'walletTrustSpeedNoPix',
  guestSheetBody: 'guestSheetBodyNoPix',
  guestWalletGateBody: 'guestWalletGateBodyNoPix',
  faqSearchPlaceholder: 'faqSearchPlaceholderNoPix',
  faq_start_fig: 'faq_start_fig_nopix',
  faq_deposit_tldr: 'faq_deposit_tldr_nopix',
  faq_deposit_fig: 'faq_deposit_fig_nopix',
  faq_deposit_points: 'faq_deposit_points_nopix',
  // The alias lists keep the `_keys` suffix: the i18n checker exempts search
  // aliases from its banned-word rules, and "top up" / "sacar plata" are there
  // precisely because players type them.
  faq_deposit_keys: 'faq_deposit_nopix_keys',
  faq_withdraw_tldr: 'faq_withdraw_tldr_nopix',
  faq_withdraw_fig: 'faq_withdraw_fig_nopix',
  faq_withdraw_points: 'faq_withdraw_points_nopix',
  faq_withdraw_keys: 'faq_withdraw_nopix_keys',
  faq_currencies_tldr: 'faq_currencies_tldr_nopix',
  faq_currencies_fig: 'faq_currencies_fig_nopix',
  faq_currencies_points: 'faq_currencies_points_nopix',
  faq_currencies_keys: 'faq_currencies_nopix_keys',
  faq_rejected_fig: 'faq_rejected_fig_nopix',
  // The legal FAQ: "registered in Brazil" and "CNPJ in the footer" are Brazil's
  // facts; elsewhere the footer names Sorteia Gaming Limited and no CNPJ.
  faq_legal_tldr: 'faq_legal_tldr_nopix',
  faq_legal_fig: 'faq_legal_fig_nopix',
  faq_legal_points: 'faq_legal_points_nopix',
  // Wallet: the deposit and withdraw sheets are titled after Pix and ask for a
  // "Pix key"; Argentina pays out to a CVU/CBU and the neutral word is "bank
  // account". The deposit title's twin is the plain "Deposit" the wallet
  // already has — the sheet appends the provider's network itself.
  pixDepositTitle: 'walletDeposit',
  pixWithdrawTitle: 'pixWithdrawTitleNoPix',
  pixWithdrawChave: 'pixWithdrawChaveNoPix',
  pixWithdrawConfirmSub: 'pixWithdrawConfirmSubNoPix',
  pixWithdrawReceivedSub: 'pixWithdrawReceivedSubNoPix',
  payeeSavedLabel: 'payeeSavedLabelNoPix',
  payeeCtaTitle: 'payeeCtaTitleNoPix',
  payeeCtaBody: 'payeeCtaBodyNoPix',
  payeeCtaButton: 'payeeCtaButtonNoPix',
  payeeFormTitle: 'payeeFormTitleNoPix',
  payeeFormEditTitle: 'payeeFormEditTitleNoPix',
  payeeFieldType: 'payeeFieldTypeNoPix',
  raffleCreditedWallet: 'raffleCreditedWalletNoPix',
  // "Brazilian regulations require…" on the identity-verification sheet.
  kycRequiredBody: 'kycRequiredBodyNoPix',
} as const satisfies Partial<Record<MessageKey, MessageKey>>;

/** The keys a screen must route through {@link marketCopyKey} rather than `t()` directly. */
export type PixCopyKey = keyof typeof PIX_FREE_TWIN;

export const PIX_COPY_KEYS = Object.keys(PIX_FREE_TWIN) as PixCopyKey[];

/**
 * Whether this market pays by Pix — i.e. whether the Pix-flavoured copy applies.
 *
 * Only Brazil. An unknown or missing code is *not* Brazil: a screen that has not
 * learned its country yet must not advertise a rail the player may not have.
 */
export function usesPix(countryCode: string | null | undefined): boolean {
  return String(countryCode || '').toUpperCase() === 'BR';
}

/** The key to render for `key` in this market: itself in Brazil, its Pix-free twin elsewhere. */
export function marketCopyKey(key: MessageKey, countryCode: string | null | undefined): MessageKey {
  if (usesPix(countryCode)) return key;
  return (PIX_FREE_TWIN as Partial<Record<MessageKey, MessageKey>>)[key] ?? key;
}

/**
 * The legal lines under the auth screens, in reading order: the company the
 * player is contracting with, then the domain with the market's own endonym.
 *
 * Brazil is Sorteia Brasil Ltda (with its CNPJ on the legal pages). Every other
 * market is Sorteia Gaming Limited — product, 2026-09-15, after D-02/D-04
 * showed an Argentine the Brazilian company under "Argentina · ARS". The
 * Brazilian entity is not the counterparty outside Brazil, so it never appears
 * there.
 */
export function authLegalLines(country: CountryConfig, t: (key: MessageKey) => string): string[] {
  const domain = `sorteia.com · ${country.name}`;
  return [t(usesPix(country.code) ? 'cnpjLine' : 'intlEntityLine'), domain];
}
